// Cel/toon shading conversion — TypeScript port of v1 js/toon-shading.js.
// Swaps Lambert/Phong/Standard materials for MeshToonMaterial with a shared
// stepped gradient ramp; cheaper than Standard and gives the flat-colored
// geometry an intentional look. Opt out via userData.noToon on an object
// (skips its subtree) or on a material.

import * as THREE from 'three';

let gradientMap: THREE.DataTexture | null = null;

export function getToonGradientMap(): THREE.DataTexture {
  if (gradientMap) return gradientMap;
  // Four-step ramp: deep shadow, shadow, lit, highlight (v1 values)
  const data = new Uint8Array([100, 160, 215, 255]);
  gradientMap = new THREE.DataTexture(data, 4, 1, THREE.RedFormat);
  gradientMap.minFilter = THREE.NearestFilter;
  gradientMap.magFilter = THREE.NearestFilter;
  gradientMap.needsUpdate = true;
  return gradientMap;
}

export const toonMaterialCache = new Map<THREE.Material, THREE.Material>();

const CONVERTIBLE_TYPES = new Set([
  'MeshPhongMaterial',
  'MeshLambertMaterial',
  'MeshStandardMaterial',
]);

type ConvertibleMaterial = THREE.Material & {
  color?: THREE.Color;
  map?: THREE.Texture | null;
  emissive?: THREE.Color;
  emissiveIntensity?: number;
};

export function toonifyMaterial(material: THREE.Material): THREE.Material {
  if (!CONVERTIBLE_TYPES.has(material.type)) return material;
  if (material.userData.noToon) return material;

  const cached = toonMaterialCache.get(material);
  if (cached) return cached;

  const src = material as ConvertibleMaterial;
  const toon = new THREE.MeshToonMaterial({
    color: src.color ? src.color.clone() : new THREE.Color(0xffffff),
    map: src.map ?? null,
    gradientMap: getToonGradientMap(),
    transparent: material.transparent,
    opacity: material.opacity,
    side: material.side,
    vertexColors: material.vertexColors,
    emissive: src.emissive ? src.emissive.clone() : new THREE.Color(0x000000),
    emissiveIntensity: src.emissiveIntensity ?? 1,
  });
  if (material.alphaTest) toon.alphaTest = material.alphaTest;

  toonMaterialCache.set(material, toon);
  return toon;
}

export function toonifyObject(root: THREE.Object3D): THREE.Object3D {
  if (root.userData.noToon) return root;

  if (root instanceof THREE.Mesh) {
    const mat: unknown = root.material;
    if (Array.isArray(mat)) {
      root.material = mat.map((m) => toonifyMaterial(m as THREE.Material));
    } else if (mat) {
      root.material = toonifyMaterial(mat as THREE.Material);
    }
  }
  for (const child of root.children) toonifyObject(child);
  return root;
}

/** Convert everything added to the scene from now on; returns a sweep function
 *  for meshes attached to already-added groups later (recycled segments). */
export function installToonPipeline(scene: THREE.Scene): () => void {
  const originalAdd = scene.add.bind(scene);
  scene.add = (...objects: THREE.Object3D[]): THREE.Scene => {
    for (const obj of objects) toonifyObject(obj);
    return originalAdd(...objects);
  };
  return () => void toonifyObject(scene);
}

export function clearToonCache(): void {
  toonMaterialCache.clear();
}
